/**
 * Progressive low-res → high-res loader for collection / category cards.
 * Runs once per image; each image reaches loaded-highres and never re-enters.
 *
 * The <img> ships with no src (data-lowres / data-highres only), and a
 * src-less or failed <img> draws the browser's broken-image icon and its alt
 * text - so component-pane-cards.css keeps it invisible until it carries
 * loaded-lowres / loaded-highres. A load that fails outright gets
 * load-failed and simply stays invisible.
 *
 * Starts as soon as the DOM is parsed (it used to wait for window load, which
 * left the tiles empty until every last script and image on the page had
 * finished), and picks up cards inserted later (cart drawer re-renders,
 * section rendering) through a MutationObserver.
 */
(function () {
  var SELECTOR = '.collection-card-progressive, .featured-category-card-progressive';

  function upgradeImage(img) {
    if (img.dataset.progressiveDone === 'true' || img.dataset.progressiveLoading === 'true') {
      return;
    }

    var lowRes = img.getAttribute('data-lowres');
    var highRes = img.getAttribute('data-highres');
    if (!lowRes) {
      return;
    }

    img.dataset.progressiveLoading = 'true';
    var picture = img.parentElement;
    var webpSource = picture && picture.querySelector('source[type="image/webp"]');
    var lowResOk = false;

    function markDone() {
      img.dataset.progressiveDone = 'true';
      img.dataset.progressiveLoading = '';
      img.classList.remove('loading', 'loaded-lowres');
      img.classList.add('loaded-highres');
    }

    function markFailed() {
      img.dataset.progressiveDone = 'true';
      img.dataset.progressiveLoading = '';
      img.classList.remove('loading');
      img.classList.add('load-failed');
    }

    function applyHighRes() {
      if (!highRes || img.dataset.progressiveDone === 'true') {
        if (lowResOk) markDone();
        else markFailed();
        return;
      }

      var preloader = new Image();
      preloader.onload = function () {
        img.addEventListener(
          'load',
          function () {
            markDone();
            if (webpSource && webpSource.dataset.srcset) {
              webpSource.srcset = webpSource.dataset.srcset;
            }
          },
          { once: true }
        );
        img.src = highRes;
      };
      // High-res failed: keep the low-res if it loaded, otherwise nothing.
      preloader.onerror = function () {
        if (lowResOk) markDone();
        else markFailed();
      };
      preloader.src = highRes;
    }

    // Low-res failed: go straight for the high-res.
    function onLowResError() {
      img.removeEventListener('load', onLowResReady);
      applyHighRes();
    }

    function onLowResReady() {
      img.removeEventListener('error', onLowResError);
      if (img.dataset.progressiveDone === 'true') {
        return;
      }
      lowResOk = true;
      img.classList.remove('loading');
      img.classList.add('loaded-lowres');
      applyHighRes();
    }

    img.classList.add('loading');

    if (img.src === lowRes && img.complete && img.naturalWidth > 0) {
      onLowResReady();
      return;
    }

    img.addEventListener('load', onLowResReady, { once: true });
    img.addEventListener('error', onLowResError, { once: true });
    img.src = lowRes;
  }

  function upgradeWithin(root) {
    if (root.matches && root.matches(SELECTOR)) upgradeImage(root);
    if (root.querySelectorAll) root.querySelectorAll(SELECTOR).forEach(upgradeImage);
  }

  function init() {
    upgradeWithin(document);
    if (!window.MutationObserver) return;
    new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) upgradeWithin(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
